// IATA special service request codes offered on a flight sector.
//
// ⚠️ THESE CANNOT BE SENT TO THE AIRLINE THROUGH THE NUITEE API. Verified against the live API:
// `servicesAttachable` exposes only `seat` and `baggage` categories across every carrier tested
// (610 seat services, 15 baggage, zero assistance), and SSR-shaped fields on the prebook passenger
// — `specialServiceRequests`, `ssrCodes`, `ssr`, `remarks`, `specialAssistance` — are all accepted
// with HTTP 200 and then silently discarded. None come back on the booking, while the known-good
// `loyaltyPrograms` control does.
//
// That silent acceptance is the danger: sending WCHC would look successful while the airline was
// never told, and the traveller would arrive expecting a chair nobody had booked. So the portal
// treats assistance as a task to be arranged with the airline directly and tracked here, rather
// than pretending the booking carries it.
//
// Shared between the trip form (where they are chosen) and the flight flow (where the person
// booking is stopped and told to arrange them).

export const FLIGHT_SSR_OPTIONS = [
  { code: 'WCHR', label: 'Wheelchair — can walk short distances (to/from gate)' },
  { code: 'WCHP', label: 'Wheelchair — can walk to seat but not long distances' },
  { code: 'WCHC', label: 'Wheelchair — cannot walk at all (requires full assistance)' },
  { code: 'WCBD', label: 'Dry-cell battery wheelchair (cabin storage)' },
  { code: 'WCBW', label: 'Wet-cell battery wheelchair (hold only)' },
  { code: 'WCMP', label: 'Manual collapsible wheelchair' },
  { code: 'BLND', label: 'Blind / visually impaired traveller' },
  { code: 'DEAF', label: 'Deaf / hearing impaired traveller' },
  { code: 'DPNA', label: 'Traveller with intellectual or developmental disability requiring assistance' },
  { code: 'PETC', label: 'Emotional support or assistance animal in cabin' },
  { code: 'UMNR', label: 'Unaccompanied minor' },
  { code: 'OTHER', label: 'Other — specify below', other: true },
];

const BY_CODE = Object.fromEntries(FLIGHT_SSR_OPTIONS.map(o => [o.code, o]));

/** Human label for a code, falling back to the code itself so an unknown one is never hidden. */
export function ssrLabel(code) {
  return BY_CODE[code]?.label || code;
}

/**
 * Codes that need the airline told, with their labels. `OTHER` is folded into its free text so the
 * list reads as instructions rather than as jargon.
 */
export function ssrRequirements(codes = [], otherText = '') {
  return (codes || []).map(code =>
    code === 'OTHER'
      ? { code, label: (otherText || '').trim() || 'Other assistance — see the trip notes' }
      : { code, label: ssrLabel(code) });
}

/**
 * Codes the airline must be notified of in advance, where turning up without prior arrangement can
 * mean being refused carriage rather than merely inconvenienced. Battery-powered wheelchairs carry
 * dangerous-goods rules, and assistance animals need documentation checked before travel.
 */
const ADVANCE_NOTICE = new Set(['WCHC', 'WCBD', 'WCBW', 'PETC', 'UMNR']);

export function needsAdvanceNotice(codes = []) {
  return (codes || []).some(c => ADVANCE_NOTICE.has(c));
}

/** Does this sector have assistance that someone still has to arrange with the airline? */
export function hasUnarrangedSsr(sector) {
  const codes = sector?.specialAssistance || [];
  if (codes.length === 0) return false;
  return !(sector?.ssrArrangedRef || '').trim();
}
